import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import {
  Droplets,
  Plus,
  Trash2,
  Syringe,
  Sun,
  Coffee,
  Moon,
  Cookie,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Calculator,
  RefreshCw,
  Info,
  Brain,
  FileText,
  Loader2,
  Printer,
  Download,
  Activity,
  Pill,
  Scale,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const MEALTIME_CONFIG = {
  fasting: { label: 'Fasting', icon: Sun, color: 'text-slate-400', bg: 'bg-slate-500/20' },
  breakfast: { label: 'Breakfast', icon: Coffee, color: 'text-amber-400', bg: 'bg-amber-500/20' },
  lunch: { label: 'Lunch', icon: Sun, color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  dinner: { label: 'Dinner', icon: Moon, color: 'text-indigo-400', bg: 'bg-indigo-500/20' },
  snack: { label: 'Snack', icon: Cookie, color: 'text-pink-400', bg: 'bg-pink-500/20' },
  bedtime: { label: 'Bedtime', icon: Moon, color: 'text-purple-400', bg: 'bg-purple-500/20' },
};

function getThresholds(unit, timing) {
  // Before meal / fasting: 4.5-7.0 mmol/l (81-126 mg/dl)
  // After meal (2h): 4.5-9.0 mmol/l (81-162 mg/dl)
  const isAfter = timing === 'after';
  if (unit === 'mg/dl') {
    return { low: 81, high: isAfter ? 162 : 126 };
  }
  return { low: 4.5, high: isAfter ? 9.0 : 7.0 };
}

function getStatusColor(value, unit, timing) {
  const { low, high } = getThresholds(unit, timing);
  if (value < low) return 'text-red-400';
  if (value > high) return 'text-orange-400';
  return 'text-emerald-400';
}

function getStatusIcon(value, unit, timing) {
  const { low, high } = getThresholds(unit, timing);
  if (value < low) return TrendingDown;
  if (value > high) return TrendingUp;
  return Minus;
}

// === AI ADVICE PANEL ===
function AiAdvicePanel({ diabetesType, unit }) {
  const [advice, setAdvice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataSummary, setDataSummary] = useState(null);

  const fetchAdvice = async () => {
    try {
      setLoading(true);
      setAdvice(null);
      const medications = JSON.parse(localStorage.getItem('gym_notebook_medications') || '[]');
      const healthConditions = localStorage.getItem('health_conditions') || '';
      const data = await api.post('/diabetes/ai-advice', {
        diabetes_type: diabetesType,
        unit,
        health_conditions: healthConditions || null,
        medications: medications.length > 0 ? medications : null,
      });
      setAdvice(data.advice);
      setDataSummary(data.data_summary);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to get AI advice');
    } finally {
      setLoading(false);
    }
  };

  // Simple markdown renderer
  const renderMarkdown = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      if (line.startsWith('### ')) return <h3 key={i} className="text-white font-semibold text-base mt-3 mb-1">{line.slice(4)}</h3>;
      if (line.startsWith('## ')) return <h2 key={i} className="text-white font-bold text-lg mt-4 mb-1">{line.slice(3)}</h2>;
      if (line.startsWith('# ')) return <h1 key={i} className="text-white font-bold text-xl mt-4 mb-2">{line.slice(2)}</h1>;
      if (line.startsWith('**') && line.endsWith('**')) return <p key={i} className="text-white font-semibold text-base mt-2">{line.slice(2, -2)}</p>;
      if (line.startsWith('- ')) return <li key={i} className="text-slate-300 text-sm ml-4 list-disc">{formatBold(line.slice(2))}</li>;
      if (line.startsWith('* ')) return <li key={i} className="text-slate-300 text-sm ml-4 list-disc">{formatBold(line.slice(2))}</li>;
      if (line.match(/^\d+\./)) return <li key={i} className="text-slate-300 text-sm ml-4 list-decimal">{formatBold(line.replace(/^\d+\.\s*/, ''))}</li>;
      if (line.trim() === '') return <div key={i} className="h-1" />;
      return <p key={i} className="text-slate-300 text-sm">{formatBold(line)}</p>;
    });
  };

  const formatBold = (text) => {
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, i) => i % 2 === 1 ? <strong key={i} className="text-white">{part}</strong> : part);
  };

  return (
    <div className="space-y-4" data-testid="ai-advice-panel">
      {/* Generate button */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardContent className="p-4 text-center space-y-3">
          <Brain className="h-8 w-8 text-violet-400 mx-auto" />
          <div>
            <h3 className="text-white font-semibold text-base">AI Health Advisor</h3>
            <p className="text-slate-400 text-sm mt-1">
              Get personalized dietary and blood sugar advice based on your last 7 days of data
            </p>
          </div>
          <Button
            onClick={fetchAdvice}
            disabled={loading}
            className="bg-violet-600 hover:bg-violet-500 text-white w-full"
            data-testid="get-ai-advice-btn"
          >
            {loading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyzing your data...</>
            ) : (
              <><Brain className="h-4 w-4 mr-2" /> Get AI Advice</>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Data summary */}
      {dataSummary && (
        <div className="grid grid-cols-4 gap-2" data-testid="advice-data-summary">
          <div className="p-2 bg-slate-800/50 rounded-lg text-center">
            <p className="text-[11px] text-slate-400">Readings</p>
            <p className="text-base font-bold text-white">{dataSummary.bs_readings}</p>
          </div>
          <div className="p-2 bg-slate-800/50 rounded-lg text-center">
            <p className="text-[11px] text-slate-400">Nut. Days</p>
            <p className="text-base font-bold text-white">{dataSummary.nutrition_days}</p>
          </div>
          <div className="p-2 bg-red-500/10 rounded-lg text-center">
            <p className="text-[11px] text-slate-400">Low</p>
            <p className="text-base font-bold text-red-400">{dataSummary.low_count}</p>
          </div>
          <div className="p-2 bg-orange-500/10 rounded-lg text-center">
            <p className="text-[11px] text-slate-400">High</p>
            <p className="text-base font-bold text-orange-400">{dataSummary.high_count}</p>
          </div>
        </div>
      )}

      {/* Advice content */}
      {advice && (
        <Card className="bg-slate-800/50 border-violet-500/20" data-testid="ai-advice-content">
          <CardContent className="p-4">
            <div className="prose prose-invert prose-sm max-w-none">
              {renderMarkdown(advice)}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// === DOCTOR REPORT PANEL ===
function DoctorReportPanel({ unit }) {
  const [period, setPeriod] = useState('2weeks');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const reportRef = useRef(null);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.get(`/diabetes/report?period=${period}`);
      setReport(data);
    } catch (err) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handlePrint = () => {
    const content = reportRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html><head><title>Diabetes Report</title>
      <style>
        body { font-family: -apple-system, sans-serif; padding: 24px; color: #1a1a1a; max-width: 700px; margin: 0 auto; }
        h1 { font-size: 20px; border-bottom: 2px solid #333; padding-bottom: 8px; }
        h2 { font-size: 16px; margin-top: 20px; color: #333; }
        .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin: 12px 0; }
        .stat { border: 1px solid #ddd; border-radius: 8px; padding: 12px; text-align: center; }
        .stat .label { font-size: 10px; color: #666; text-transform: uppercase; }
        .stat .value { font-size: 18px; font-weight: bold; margin-top: 4px; }
        .high { color: #ea580c; }
        .low { color: #dc2626; }
        .normal { color: #16a34a; }
        table { width: 100%; border-collapse: collapse; margin: 12px 0; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
        th { background: #f5f5f5; font-weight: 600; }
        .disclaimer { font-size: 10px; color: #999; margin-top: 24px; border-top: 1px solid #eee; padding-top: 8px; }
        @media print { body { padding: 0; } }
      </style>
      </head><body>
      <h1>Diabetes Report</h1>
      <p style="font-size:12px;color:#666;">Period: ${report?.start_date} to ${report?.end_date} (${period === '2weeks' ? '2 Weeks' : period === '1month' ? '1 Month' : '3 Months'})</p>
      <p style="font-size:12px;color:#666;">Generated: ${new Date().toLocaleDateString()}</p>

      <h2>Blood Sugar Overview</h2>
      <div class="grid">
        <div class="stat"><div class="label">Average</div><div class="value">${report?.average_blood_sugar || '-'} ${unit}</div></div>
        <div class="stat"><div class="label">Min</div><div class="value">${report?.min_blood_sugar || '-'}</div></div>
        <div class="stat"><div class="label">Max</div><div class="value">${report?.max_blood_sugar || '-'}</div></div>
      </div>
      <div class="grid">
        <div class="stat"><div class="label">In Range</div><div class="value normal">${report?.in_range_pct || 0}%</div></div>
        <div class="stat"><div class="label">High</div><div class="value high">${report?.high_pct || 0}%</div></div>
        <div class="stat"><div class="label">Low</div><div class="value low">${report?.low_pct || 0}%</div></div>
      </div>

      <h2>Insulin Summary</h2>
      <table>
        <tr><th>Metric</th><th>Average Daily</th></tr>
        <tr><td>Long-acting</td><td>${report?.avg_long_acting ?? '-'} u</td></tr>
        <tr><td>Short-acting (total)</td><td>${report?.avg_short_acting ?? '-'} u</td></tr>
      </table>

      ${report?.avg_short_by_mealtime && Object.keys(report.avg_short_by_mealtime).length > 0 ? `
      <h2>Short-Acting Insulin by Mealtime</h2>
      <table>
        <tr><th>Mealtime</th><th>Avg Dose (u)</th></tr>
        ${Object.entries(report.avg_short_by_mealtime).map(([m, v]) => `<tr><td>${m}</td><td>${v}</td></tr>`).join('')}
      </table>` : ''}

      ${report?.avg_bs_by_mealtime && Object.keys(report.avg_bs_by_mealtime).length > 0 ? `
      <h2>Blood Sugar by Mealtime</h2>
      <table>
        <tr><th>Mealtime</th><th>Avg (${unit})</th></tr>
        ${Object.entries(report.avg_bs_by_mealtime).map(([m, v]) => `<tr><td>${m}</td><td>${v}</td></tr>`).join('')}
      </table>` : ''}

      ${report?.avg_carbs_by_mealtime && Object.keys(report.avg_carbs_by_mealtime).length > 0 ? `
      <h2>Nutrition by Mealtime (Daily Avg)</h2>
      <table>
        <tr><th>Mealtime</th><th>Carbs (g)</th><th>Calories</th></tr>
        ${Object.entries(report.avg_carbs_by_mealtime).map(([m, v]) => `<tr><td>${m}</td><td>${v}</td><td>${report.avg_cal_by_mealtime?.[m] || '-'}</td></tr>`).join('')}
      </table>` : ''}

      ${report?.weight || report?.waist ? `
      <h2>Weight & Waist Changes</h2>
      <table>
        <tr><th>Metric</th><th>Start</th><th>End</th><th>Change</th><th>Min</th><th>Max</th></tr>
        ${report.weight ? `<tr><td>Weight (${report.weight.unit})</td><td>${report.weight.start}</td><td>${report.weight.end}</td><td style="color:${report.weight.change > 0 ? '#ea580c' : report.weight.change < 0 ? '#16a34a' : '#666'}">${report.weight.change > 0 ? '+' : ''}${report.weight.change}</td><td>${report.weight.min}</td><td>${report.weight.max}</td></tr>` : ''}
        ${report.waist ? `<tr><td>Waist (${report.waist.unit})</td><td>${report.waist.start}</td><td>${report.waist.end}</td><td style="color:${report.waist.change > 0 ? '#ea580c' : report.waist.change < 0 ? '#16a34a' : '#666'}">${report.waist.change > 0 ? '+' : ''}${report.waist.change}</td><td>${report.waist.min}</td><td>${report.waist.max}</td></tr>` : ''}
      </table>` : ''}

      <p style="font-size:12px;margin-top:16px;">Total readings: ${report?.total_readings || 0} | Nutrition days tracked: ${report?.nutrition_days_tracked || 0}${report?.weight ? ` | Weight measurements: ${report.weight.count}` : ''}${report?.waist ? ` | Waist measurements: ${report.waist.count}` : ''}</p>
      <p class="disclaimer">This report is generated from self-reported data and is intended for informational purposes only. Please consult your healthcare provider for medical decisions.</p>
      </body></html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const periods = [
    { value: '2weeks', label: '2 Weeks' },
    { value: '1month', label: '1 Month' },
    { value: '3months', label: '3 Months' },
  ];

  const mealOrder = ['fasting', 'breakfast', 'lunch', 'dinner', 'snack', 'bedtime'];
  const sortMeals = (obj) => {
    const sorted = {};
    mealOrder.forEach(m => { if (obj[m] !== undefined) sorted[m] = obj[m]; });
    Object.keys(obj).forEach(m => { if (!sorted[m]) sorted[m] = obj[m]; });
    return sorted;
  };

  return (
    <div className="space-y-4" data-testid="doctor-report-panel" ref={reportRef}>
      {/* Period selector */}
      <div className="flex gap-1 bg-slate-800/50 rounded-lg p-1">
        {periods.map(p => (
          <button
            key={p.value}
            onClick={() => setPeriod(p.value)}
            className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all ${
              period === p.value ? 'bg-teal-500/20 text-teal-300' : 'text-slate-400 hover:text-white'
            }`}
            data-testid={`period-${p.value}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-400 text-sm">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Generating report...
        </div>
      ) : !report || report.total_readings === 0 ? (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">No blood sugar data for this period</p>
          <p className="text-slate-500 text-xs mt-1">Start logging in the Diary tab to generate reports</p>
        </div>
      ) : (
        <>
          {/* Print button */}
          <div className="flex justify-end">
            <Button
              onClick={handlePrint}
              size="sm"
              variant="outline"
              className="text-sm border-slate-600 text-slate-300 hover:text-white"
              data-testid="print-report-btn"
            >
              <Printer className="h-4 w-4 mr-1.5" /> Print / Export PDF
            </Button>
          </div>

          {/* Blood Sugar Overview */}
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardContent className="p-4 space-y-3">
              <h3 className="text-base font-semibold text-white flex items-center gap-2">
                <Droplets className="h-4 w-4 text-rose-400" /> Blood Sugar Overview
              </h3>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2.5 bg-slate-900/50 rounded-lg">
                  <p className="text-[11px] text-slate-400 uppercase">Average</p>
                  <p className="text-xl font-bold text-white">{report.average_blood_sugar}</p>
                  <p className="text-xs text-slate-400">{unit}</p>
                </div>
                <div className="p-2.5 bg-slate-900/50 rounded-lg">
                  <p className="text-[11px] text-slate-400 uppercase">Min</p>
                  <p className="text-xl font-bold text-sky-400">{report.min_blood_sugar}</p>
                </div>
                <div className="p-2.5 bg-slate-900/50 rounded-lg">
                  <p className="text-[11px] text-slate-400 uppercase">Max</p>
                  <p className="text-xl font-bold text-amber-400">{report.max_blood_sugar}</p>
                </div>
              </div>

              {/* Range distribution bar */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">Range Distribution ({report.total_readings} readings)</span>
                </div>
                <div className="flex h-6 rounded-full overflow-hidden bg-slate-900">
                  {report.in_range_pct > 0 && (
                    <div className="bg-emerald-500 flex items-center justify-center text-[10px] text-white font-semibold"
                      style={{ width: `${report.in_range_pct}%` }}>{report.in_range_pct}%</div>
                  )}
                  {report.high_pct > 0 && (
                    <div className="bg-orange-500 flex items-center justify-center text-[10px] text-white font-semibold"
                      style={{ width: `${report.high_pct}%` }}>{report.high_pct}%</div>
                  )}
                  {report.low_pct > 0 && (
                    <div className="bg-red-500 flex items-center justify-center text-[10px] text-white font-semibold"
                      style={{ width: `${report.low_pct}%` }}>{report.low_pct}%</div>
                  )}
                </div>
                <div className="flex gap-3 text-xs">
                  <span className="text-emerald-400 flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> In Range {report.in_range_count}</span>
                  <span className="text-orange-400 flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-orange-500" /> High {report.high_count}</span>
                  <span className="text-red-400 flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Low {report.low_count}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Insulin Summary */}
          {(report.avg_long_acting || report.avg_short_acting) && (
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardContent className="p-4 space-y-3">
                <h3 className="text-base font-semibold text-white flex items-center gap-2">
                  <Syringe className="h-4 w-4 text-sky-400" /> Insulin Summary
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-violet-500/10 rounded-lg text-center">
                    <p className="text-xs text-slate-400">Avg Daily Long-acting</p>
                    <p className="text-2xl font-bold text-violet-300">{report.avg_long_acting ?? '-'}<span className="text-sm ml-0.5">u</span></p>
                  </div>
                  <div className="p-3 bg-sky-500/10 rounded-lg text-center">
                    <p className="text-xs text-slate-400">Avg Daily Short-acting</p>
                    <p className="text-2xl font-bold text-sky-300">{report.avg_short_acting ?? '-'}<span className="text-sm ml-0.5">u</span></p>
                  </div>
                </div>
                {/* Short-acting by mealtime */}
                {Object.keys(report.avg_short_by_mealtime || {}).length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs text-slate-400 uppercase font-medium">Short-acting by Mealtime</p>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(sortMeals(report.avg_short_by_mealtime)).map(([meal, val]) => (
                        <div key={meal} className="flex items-center justify-between p-2.5 bg-slate-900/50 rounded-lg">
                          <span className="text-sm text-slate-300 capitalize">{meal}</span>
                          <span className="text-sm font-bold text-sky-300">{val}u</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Blood Sugar by Mealtime */}
          {Object.keys(report.avg_bs_by_mealtime || {}).length > 0 && (
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardContent className="p-4 space-y-3">
                <h3 className="text-base font-semibold text-white flex items-center gap-2">
                  <Activity className="h-4 w-4 text-rose-400" /> Avg Blood Sugar by Mealtime
                </h3>
                <div className="space-y-2">
                  {Object.entries(sortMeals(report.avg_bs_by_mealtime)).map(([meal, val]) => {
                    const { low: lowT, high: highT } = getThresholds(unit, 'before');
                    const color = val < lowT ? 'text-red-400' : val > highT ? 'text-orange-400' : 'text-emerald-400';
                    return (
                      <div key={meal} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                        <span className="text-sm text-slate-300 capitalize">{meal}</span>
                        <span className={`text-base font-bold ${color}`}>{val} {unit}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Nutrition by Mealtime */}
          {Object.keys(report.avg_carbs_by_mealtime || {}).length > 0 && (
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardContent className="p-4 space-y-3">
                <h3 className="text-base font-semibold text-white flex items-center gap-2">
                  <Pill className="h-4 w-4 text-amber-400" /> Avg Daily Nutrition by Mealtime
                </h3>
                <div className="space-y-2">
                  {Object.entries(sortMeals(report.avg_carbs_by_mealtime)).map(([meal, carbs]) => (
                    <div key={meal} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                      <span className="text-sm text-slate-300 capitalize">{meal}</span>
                      <div className="flex items-center gap-3 text-sm">
                        <span className="text-yellow-400 font-semibold">{carbs}g carbs</span>
                        <span className="text-orange-400">{report.avg_cal_by_mealtime?.[meal] || '-'} kcal</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Weight & Waist Changes */}
          {(report.weight || report.waist) && (
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardContent className="p-4 space-y-3">
                <h3 className="text-base font-semibold text-white flex items-center gap-2">
                  <Scale className="h-4 w-4 text-cyan-400" /> Weight & Waist Changes
                </h3>
                <div className="space-y-3">
                  {report.weight && (
                    <div className="p-3 bg-slate-900/50 rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-300 font-medium">Weight ({report.weight.unit})</span>
                        <span className={`text-sm font-bold flex items-center gap-1 ${report.weight.change > 0 ? 'text-orange-400' : report.weight.change < 0 ? 'text-emerald-400' : 'text-slate-400'}`}>
                          {report.weight.change > 0 ? <TrendingUp className="h-3.5 w-3.5" /> : report.weight.change < 0 ? <TrendingDown className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
                          {report.weight.change > 0 ? '+' : ''}{report.weight.change} {report.weight.unit}
                        </span>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <div><p className="text-[10px] text-slate-500 uppercase">Start</p><p className="text-sm font-semibold text-white">{report.weight.start}</p></div>
                        <div><p className="text-[10px] text-slate-500 uppercase">End</p><p className="text-sm font-semibold text-white">{report.weight.end}</p></div>
                        <div><p className="text-[10px] text-slate-500 uppercase">Min</p><p className="text-sm font-semibold text-sky-400">{report.weight.min}</p></div>
                        <div><p className="text-[10px] text-slate-500 uppercase">Max</p><p className="text-sm font-semibold text-amber-400">{report.weight.max}</p></div>
                      </div>
                      <p className="text-[10px] text-slate-500 text-right">{report.weight.count} measurement{report.weight.count !== 1 ? 's' : ''}</p>
                    </div>
                  )}
                  {report.waist && (
                    <div className="p-3 bg-slate-900/50 rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-300 font-medium">Waist ({report.waist.unit})</span>
                        <span className={`text-sm font-bold flex items-center gap-1 ${report.waist.change > 0 ? 'text-orange-400' : report.waist.change < 0 ? 'text-emerald-400' : 'text-slate-400'}`}>
                          {report.waist.change > 0 ? <TrendingUp className="h-3.5 w-3.5" /> : report.waist.change < 0 ? <TrendingDown className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
                          {report.waist.change > 0 ? '+' : ''}{report.waist.change} {report.waist.unit}
                        </span>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <div><p className="text-[10px] text-slate-500 uppercase">Start</p><p className="text-sm font-semibold text-white">{report.waist.start}</p></div>
                        <div><p className="text-[10px] text-slate-500 uppercase">End</p><p className="text-sm font-semibold text-white">{report.waist.end}</p></div>
                        <div><p className="text-[10px] text-slate-500 uppercase">Min</p><p className="text-sm font-semibold text-sky-400">{report.waist.min}</p></div>
                        <div><p className="text-[10px] text-slate-500 uppercase">Max</p><p className="text-sm font-semibold text-amber-400">{report.waist.max}</p></div>
                      </div>
                      <p className="text-[10px] text-slate-500 text-right">{report.waist.count} measurement{report.waist.count !== 1 ? 's' : ''}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Footer info */}
          <p className="text-[11px] text-slate-500 text-center px-4">
            Report based on {report.total_readings} blood sugar readings, {report.nutrition_days_tracked} days of nutrition data
            {report.weight ? `, ${report.weight.count} weight` : ''}{report.waist ? `, ${report.waist.count} waist` : ''} measurement{(report.weight?.count || 0) + (report.waist?.count || 0) !== 1 ? 's' : ''}.
            Period: {report.start_date} to {report.end_date}. This report is for informational purposes only.
          </p>
        </>
      )}
    </div>
  );
}

export default function DiabetesDiary({ onBack, onNavigate }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [showAddForm, setShowAddForm] = useState(false);

  // Settings from localStorage
  const unit = localStorage.getItem('diabetes_unit') || 'mmol/l';
  const insulinEnabled = localStorage.getItem('diabetes_insulin') === 'true';
  const diabetesType = localStorage.getItem('diabetes_type') || 'type2';

  // Tab state
  const [activeTab, setActiveTab] = useState('diary');

  // Calculator state
  const [weeklyAvg, setWeeklyAvg] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [manualICR, setManualICR] = useState('');
  const [manualISF, setManualISF] = useState('');
  const [targetGlucose, setTargetGlucose] = useState(unit === 'mg/dl' ? '120' : '6.0');
  const [currentGlucose, setCurrentGlucose] = useState('');
  const [mealCarbs, setMealCarbs] = useState('');

  const fetchWeeklyAvg = useCallback(async () => {
    try {
      setCalcLoading(true);
      const data = await api.get('/diabetes/weekly-averages');
      setWeeklyAvg(data);
    } catch (err) {
      console.error('Failed to fetch weekly averages:', err);
    } finally {
      setCalcLoading(false);
    }
  }, []);

  // Auto-calculated ICR & ISF from weekly averages
  const autoICR = weeklyAvg?.has_data && weeklyAvg.total_daily_insulin > 0
    ? Math.round(500 / weeklyAvg.total_daily_insulin) : null;
  const isfDivisor = diabetesType === 'type1' ? 75 : 90;
  const autoISF = weeklyAvg?.has_data && weeklyAvg.total_daily_insulin > 0
    ? parseFloat((isfDivisor / weeklyAvg.total_daily_insulin).toFixed(1)) : null;
  // Use manual values if set, otherwise auto
  const activeICR = manualICR ? parseFloat(manualICR) : autoICR;
  const activeISF = manualISF ? parseFloat(manualISF) : autoISF;
  // Bolus calculation
  const carbDose = (activeICR && mealCarbs) ? parseFloat((parseFloat(mealCarbs) / activeICR).toFixed(1)) : null;
  const targetVal = parseFloat(targetGlucose) || (unit === 'mg/dl' ? 120 : 6.0);
  const correctionDose = (activeISF && currentGlucose && parseFloat(currentGlucose) > targetVal)
    ? parseFloat(((parseFloat(currentGlucose) - targetVal) / activeISF).toFixed(1)) : 0;
  const totalBolus = carbDose !== null ? parseFloat(((carbDose || 0) + correctionDose).toFixed(1)) : null;

  // New entry form
  const [form, setForm] = useState({
    value: '',
    mealtime: 'breakfast',
    timing: 'before',
    notes: '',
    insulin_short: '',
    insulin_long: '',
  });

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.get(`/diabetes?date=${selectedDate}`);
      setEntries(data?.entries || []);
    } catch (err) {
      console.error('Failed to fetch entries:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleSubmit = async () => {
    if (!form.value) {
      toast.error('Please enter blood sugar value');
      return;
    }
    try {
      const payload = {
        value: parseFloat(form.value),
        unit,
        mealtime: form.mealtime,
        timing: form.timing,
        notes: form.notes || '',
        date: selectedDate,
      };
      if (insulinEnabled && form.insulin_short) {
        payload.insulin_short = parseFloat(form.insulin_short);
      }
      if (insulinEnabled && form.insulin_long) {
        payload.insulin_long = parseFloat(form.insulin_long);
      }
      await api.post('/diabetes', payload);
      toast.success('Blood sugar entry added');
      setShowAddForm(false);
      setForm({ value: '', mealtime: 'breakfast', timing: 'before', notes: '', insulin_short: '', insulin_long: '' });
      fetchEntries();
    } catch (err) {
      toast.error('Failed to save entry');
    }
  };

  const handleDelete = async (entryId) => {
    try {
      await api.delete(`/diabetes/${entryId}`);
      toast.success('Entry deleted');
      fetchEntries();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const changeDate = (offset) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + offset);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const isToday = selectedDate === new Date().toISOString().split('T')[0];

  // Touch swipe for date navigation
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);
  const handleTouchEnd = useCallback((e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
    // Only trigger if horizontal swipe > 60px and not mostly vertical
    if (Math.abs(dx) > 60 && dy < 80) {
      if (dx > 0) changeDate(-1); // swipe right = previous day
      else if (!isToday) changeDate(1); // swipe left = next day
    }
    touchStartX.current = null;
    touchStartY.current = null;
  }, [isToday]);

  // Calculate stats
  const values = entries.filter(e => e.value).map(e => e.value);
  const avg = values.length ? (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1) : null;
  // Timing-aware counts
  const lowCount = entries.filter(e => e.value && e.value < getThresholds(unit, e.timing).low).length;
  const highCount = entries.filter(e => e.value && e.value > getThresholds(unit, e.timing).high).length;
  const inRangeCount = values.length - lowCount - highCount;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950 border-none ios-header shadow-none" style={{ paddingTop: 'max(20px, env(safe-area-inset-top, 20px))', border: 'none' }}>
        <div className="container mx-auto px-2 sm:px-4 py-2 flex items-center justify-between max-w-full overflow-hidden">
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-rose-500 to-red-600 flex items-center justify-center shadow-lg shadow-rose-500/20 flex-shrink-0">
              <Droplets className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-base sm:text-xl font-heading font-bold tracking-tight text-white truncate">Diabetes Diary</h1>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setShowAddForm(true)} data-testid="add-blood-sugar-btn">
            <Plus className="h-5 w-5 text-white" />
          </Button>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-4 pt-4 pb-24 max-w-lg">
        {/* Tab Switcher */}
        <div className="flex gap-1 mb-4 bg-slate-800/50 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('diary')}
            className={`flex-1 py-2.5 text-xs font-semibold rounded-md transition-all flex items-center justify-center gap-1.5 ${
              activeTab === 'diary' ? 'bg-rose-500/20 text-rose-300' : 'text-slate-400 hover:text-white'
            }`}
            data-testid="tab-diary"
          >
            <Droplets className="h-3.5 w-3.5" /> Diary
          </button>
          {insulinEnabled && (
            <button
              onClick={() => { setActiveTab('calculator'); fetchWeeklyAvg(); }}
              className={`flex-1 py-2.5 text-xs font-semibold rounded-md transition-all flex items-center justify-center gap-1.5 ${
                activeTab === 'calculator' ? 'bg-sky-500/20 text-sky-300' : 'text-slate-400 hover:text-white'
              }`}
              data-testid="tab-calculator"
            >
              <Calculator className="h-3.5 w-3.5" /> Calculator
            </button>
          )}
          <button
            onClick={() => setActiveTab('advice')}
            className={`flex-1 py-2.5 text-xs font-semibold rounded-md transition-all flex items-center justify-center gap-1.5 ${
              activeTab === 'advice' ? 'bg-violet-500/20 text-violet-300' : 'text-slate-400 hover:text-white'
            }`}
            data-testid="tab-advice"
          >
            <Brain className="h-3.5 w-3.5" /> AI Advice
          </button>
          <button
            onClick={() => setActiveTab('report')}
            className={`flex-1 py-2.5 text-xs font-semibold rounded-md transition-all flex items-center justify-center gap-1.5 ${
              activeTab === 'report' ? 'bg-teal-500/20 text-teal-300' : 'text-slate-400 hover:text-white'
            }`}
            data-testid="tab-report"
          >
            <FileText className="h-3.5 w-3.5" /> Report
          </button>
        </div>

        {activeTab === 'calculator' && insulinEnabled ? (
          /* === INSULIN CALCULATOR === */
          <div className="space-y-4" data-testid="insulin-calculator">
            {/* Weekly Averages */}
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-white flex items-center gap-2">
                    <RefreshCw className="h-4 w-4 text-sky-400" /> Weekly Averages (Auto)
                  </h3>
                  <Button variant="ghost" size="sm" onClick={fetchWeeklyAvg} className="text-xs text-slate-400">
                    Refresh
                  </Button>
                </div>
                {calcLoading ? (
                  <p className="text-sm text-slate-500">Loading...</p>
                ) : weeklyAvg?.has_data ? (
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="p-2.5 bg-violet-500/10 rounded-lg">
                      <p className="text-xs text-slate-400">Long-acting avg</p>
                      <p className="text-xl font-bold text-violet-300">{weeklyAvg.avg_long_acting}u</p>
                    </div>
                    <div className="p-2.5 bg-sky-500/10 rounded-lg">
                      <p className="text-xs text-slate-400">Short-acting avg</p>
                      <p className="text-xl font-bold text-sky-300">{weeklyAvg.avg_short_acting}u</p>
                    </div>
                    <div className="p-2.5 bg-emerald-500/10 rounded-lg">
                      <p className="text-xs text-slate-400">TDI</p>
                      <p className="text-xl font-bold text-emerald-300">{weeklyAvg.total_daily_insulin}u</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No insulin data from last 7 days. Log insulin doses in the diary first.</p>
                )}
                <p className="text-[11px] text-slate-500">{weeklyAvg?.days_with_data || 0} days with data in last week</p>
              </CardContent>
            </Card>

            {/* ICR & ISF */}
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardContent className="p-4 space-y-3">
                <h3 className="text-base font-semibold text-white">Ratios (ICR & ISF)</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-400 flex items-center gap-1">
                      ICR (1u per X g carbs)
                      <span className="text-sky-500" title="500 / Total Daily Insulin"><Info className="h-3 w-3" /></span>
                    </Label>
                    <Input
                      type="number"
                      step="1"
                      placeholder={autoICR ? `Auto: ${autoICR}` : 'Enter manually'}
                      value={manualICR}
                      onChange={(e) => setManualICR(e.target.value)}
                      className="bg-slate-800 border-slate-700 text-white placeholder-slate-500 text-sm"
                      data-testid="icr-input"
                    />
                    {autoICR && <p className="text-[11px] text-sky-400">Auto: 500 / {weeklyAvg?.total_daily_insulin} = {autoICR}g per 1u</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-400 flex items-center gap-1">
                      ISF (1u lowers {unit})
                      <span className="text-sky-500" title={`${isfDivisor} / Total Daily Insulin`}><Info className="h-3 w-3" /></span>
                    </Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder={autoISF ? `Auto: ${autoISF}` : 'Enter manually'}
                      value={manualISF}
                      onChange={(e) => setManualISF(e.target.value)}
                      className="bg-slate-800 border-slate-700 text-white placeholder-slate-500 text-sm"
                      data-testid="isf-input"
                    />
                    {autoISF && <p className="text-[11px] text-sky-400">Auto: {isfDivisor} / {weeklyAvg?.total_daily_insulin} = {autoISF} {unit} ({diabetesType === 'type1' ? 'Type 1' : 'Type 2'})</p>}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bolus Calculator */}
            <Card className="bg-slate-800/50 border-sky-500/30 border-2">
              <CardContent className="p-4 space-y-3">
                <h3 className="text-base font-semibold text-white flex items-center gap-2">
                  <Calculator className="h-4 w-4 text-sky-400" /> Bolus Dose Calculator
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-400 h-8 flex items-end">Current ({unit})</Label>
                    <Input
                      type="number"
                      step={unit === 'mmol/l' ? '0.1' : '1'}
                      placeholder={unit === 'mmol/l' ? '7.5' : '135'}
                      value={currentGlucose}
                      onChange={(e) => setCurrentGlucose(e.target.value)}
                      className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
                      data-testid="current-glucose-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-400 h-8 flex items-end">Target ({unit})</Label>
                    <Input
                      type="number"
                      step={unit === 'mmol/l' ? '0.1' : '1'}
                      value={targetGlucose}
                      onChange={(e) => setTargetGlucose(e.target.value)}
                      className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
                      data-testid="target-glucose-input"
                    />
                    <p className="text-[10px] text-slate-600">OK: {unit === 'mmol/l' ? '5.5-7.0' : '100-126'}</p>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400">Meal Carbs (g)</Label>
                  <Input
                    type="number"
                    step="1"
                    placeholder="e.g., 45"
                    value={mealCarbs}
                    onChange={(e) => setMealCarbs(e.target.value)}
                    className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
                    data-testid="meal-carbs-input"
                  />
                </div>

                {/* Results */}
                {(carbDose !== null || correctionDose > 0) && (
                  <div className="mt-3 p-3 bg-sky-500/10 rounded-lg border border-sky-500/20 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Carb dose ({mealCarbs}g / {activeICR})</span>
                      <span className="text-white font-semibold">{carbDose || 0} u</span>
                    </div>
                    {correctionDose > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Correction ({currentGlucose} - {targetGlucose}) / {activeISF}</span>
                        <span className="text-orange-300 font-semibold">+{correctionDose} u</span>
                      </div>
                    )}
                    <div className="border-t border-sky-500/20 pt-2 flex justify-between">
                      <span className="text-base font-semibold text-sky-300">Total Bolus</span>
                      <span className="text-xl font-bold text-sky-300">{totalBolus} u</span>
                    </div>
                  </div>
                )}

                {!activeICR && !activeISF && (
                  <p className="text-sm text-amber-400 flex items-center gap-1">
                    <AlertTriangle className="h-3.5 w-3.5" /> Enter ICR/ISF manually or log 7 days of insulin for auto-calculation.
                  </p>
                )}

                <p className="text-[11px] text-slate-500 flex items-center gap-1 mt-2">
                  <AlertTriangle className="h-3 w-3" /> This is a recommendation only. Always consult your doctor before adjusting insulin doses.
                </p>
              </CardContent>
            </Card>
          </div>
        ) : activeTab === 'advice' ? (
          /* === AI ADVICE === */
          <AiAdvicePanel diabetesType={diabetesType} unit={unit} />
        ) : activeTab === 'report' ? (
          /* === DOCTOR REPORT === */
          <DoctorReportPanel unit={unit} />
        ) : (
        /* === DIARY VIEW === */
        <div onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        {/* Date Navigation */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <button
            onClick={() => changeDate(-1)}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/50 transition-colors"
            data-testid="date-prev-btn"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <input
            type="date"
            value={selectedDate}
            max={new Date().toISOString().split('T')[0]}
            onChange={(e) => e.target.value && setSelectedDate(e.target.value)}
            className="bg-transparent text-base font-semibold text-white focus:outline-none cursor-pointer"
            style={{ colorScheme: 'dark' }}
            data-testid="date-picker"
          />
          <button
            onClick={() => !isToday && changeDate(1)}
            disabled={isToday}
            className={`p-2 rounded-lg transition-colors ${isToday ? 'text-slate-700 cursor-not-allowed' : 'text-slate-400 hover:text-white hover:bg-slate-800/50'}`}
            data-testid="date-next-btn"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          {!isToday && (
            <button
              onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
              className="text-xs text-rose-400 hover:text-rose-300 ml-1"
              data-testid="date-today-btn"
            >
              Today
            </button>
          )}
        </div>

        {/* Daily Summary */}
        {values.length > 0 && (
          <Card className="bg-slate-800/50 border-slate-700/50 mb-4" data-testid="daily-summary">
            <CardContent className="p-4">
              <div className="grid grid-cols-4 gap-3 text-center">
                <div>
                  <p className="text-[11px] text-slate-400 uppercase">Average</p>
                  <p className={`text-xl font-bold ${avg && getStatusColor(parseFloat(avg), unit, 'before')}`}>
                    {avg}
                  </p>
                  <p className="text-xs text-slate-400">{unit}</p>
                </div>
                <div>
                  <p className="text-[11px] text-slate-400 uppercase">In Range</p>
                  <p className="text-xl font-bold text-emerald-400">{inRangeCount}</p>
                </div>
                <div>
                  <p className="text-[11px] text-slate-400 uppercase">High</p>
                  <p className="text-xl font-bold text-orange-400">{highCount}</p>
                </div>
                <div>
                  <p className="text-[11px] text-slate-400 uppercase">Low</p>
                  <p className="text-xl font-bold text-red-400">{lowCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Low sugar warning */}
        {lowCount > 0 && (
          <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg mb-4 text-red-300 text-sm" data-testid="low-sugar-warning">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>Low blood sugar detected ({unit === 'mg/dl' ? '<81 mg/dL' : '<4.5 mmol/L'}). If symptoms present, treat immediately.</span>
          </div>
        )}

        {/* Entries List */}
        {loading ? (
          <div className="text-center text-slate-500 py-8">Loading...</div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12">
            <Droplets className="h-12 w-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">No entries for this day</p>
            <Button
              onClick={() => setShowAddForm(true)}
              className="mt-4 bg-rose-600 hover:bg-rose-500 text-white"
              data-testid="add-first-entry-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> Add Blood Sugar
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="entries-list">
            {entries.map((entry) => {
              const config = MEALTIME_CONFIG[entry.mealtime] || MEALTIME_CONFIG.breakfast;
              const Icon = config.icon;
              const StatusIcon = getStatusIcon(entry.value, entry.unit || unit, entry.timing);
              return (
                <Card key={entry.entry_id} className="bg-slate-800/50 border-slate-700/50" data-testid={`entry-${entry.entry_id}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${config.bg}`}>
                          <Icon className={`h-4 w-4 ${config.color}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xl font-bold ${getStatusColor(entry.value, entry.unit || unit, entry.timing)}`}>
                              {entry.value}
                            </span>
                            <span className="text-xs text-slate-400">{entry.unit || unit}</span>
                            <StatusIcon className={`h-4 w-4 ${getStatusColor(entry.value, entry.unit || unit, entry.timing)}`} />
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm text-slate-300">{config.label}</span>
                            <span className="text-xs text-slate-500">({entry.timing})</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Insulin doses */}
                        {(entry.insulin_short || entry.insulin_long) && (
                          <div className="text-right mr-2">
                            {entry.insulin_short && (
                              <p className="text-xs text-sky-400 flex items-center gap-1">
                                <Syringe className="h-3 w-3" /> {entry.insulin_short}u fast
                              </p>
                            )}
                            {entry.insulin_long && (
                              <p className="text-xs text-violet-400 flex items-center gap-1">
                                <Syringe className="h-3 w-3" /> {entry.insulin_long}u long
                              </p>
                            )}
                          </div>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-slate-500 hover:text-red-400"
                          onClick={() => handleDelete(entry.entry_id)}
                          data-testid={`delete-entry-${entry.entry_id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    {entry.notes && (
                      <p className="text-xs text-slate-400 mt-1.5 ml-12">{entry.notes}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Add Entry Form (Modal-style overlay) */}
        {showAddForm && (
          <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={() => setShowAddForm(false)}>
            <div
              className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md p-5 space-y-4 max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="add-entry-form"
            >
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Droplets className="h-4 w-4 text-rose-400" />
                Add Blood Sugar
              </h3>

              {/* Blood Sugar Value */}
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm">Blood Sugar ({unit})</Label>
                <Input
                  type="number"
                  step={unit === 'mmol/l' ? '0.1' : '1'}
                  placeholder={unit === 'mmol/l' ? 'e.g., 5.6' : 'e.g., 100'}
                  value={form.value}
                  onChange={(e) => setForm(prev => ({ ...prev, value: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white text-lg font-semibold placeholder-slate-600"
                  autoFocus
                  data-testid="blood-sugar-value-input"
                />
              </div>

              {/* Mealtime */}
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm">Mealtime</Label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(MEALTIME_CONFIG).map(([key, cfg]) => {
                    const MealIcon = cfg.icon;
                    return (
                      <button
                        key={key}
                        onClick={() => setForm(prev => ({ ...prev, mealtime: key }))}
                        className={`p-2 rounded-lg border text-xs font-medium transition-all flex flex-col items-center gap-1 ${
                          form.mealtime === key
                            ? `border-rose-500 bg-rose-500/20 ${cfg.color}`
                            : 'border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-600'
                        }`}
                        data-testid={`mealtime-${key}`}
                      >
                        <MealIcon className="h-3.5 w-3.5" />
                        {cfg.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Timing */}
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm">Timing</Label>
                <div className="grid grid-cols-2 gap-2">
                  {['before', 'after'].map(t => (
                    <button
                      key={t}
                      onClick={() => setForm(prev => ({ ...prev, timing: t }))}
                      className={`p-2.5 rounded-lg border text-xs font-medium transition-all ${
                        form.timing === t
                          ? 'border-rose-500 bg-rose-500/20 text-rose-400'
                          : 'border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-600'
                      }`}
                      data-testid={`timing-${t}`}
                    >
                      {t === 'before' ? 'Before Meal' : 'After Meal'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Insulin doses (if enabled) */}
              {insulinEnabled && (
                <div className="space-y-3 p-3 bg-sky-500/5 border border-sky-500/20 rounded-lg">
                  <Label className="text-sky-300 text-sm flex items-center gap-1.5">
                    <Syringe className="h-3.5 w-3.5" /> Insulin
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-[10px] text-slate-400">Short-acting (units)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        placeholder=""
                        value={form.insulin_short}
                        onChange={(e) => setForm(prev => ({ ...prev, insulin_short: e.target.value }))}
                        className="bg-slate-800 border-slate-700 text-white placeholder-slate-600"
                        data-testid="insulin-short-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-slate-400">Long-acting (units)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        placeholder=""
                        value={form.insulin_long}
                        onChange={(e) => setForm(prev => ({ ...prev, insulin_long: e.target.value }))}
                        className="bg-slate-800 border-slate-700 text-white placeholder-slate-600"
                        data-testid="insulin-long-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm">Notes (optional)</Label>
                <Input
                  placeholder="e.g., felt dizzy, after exercise..."
                  value={form.notes}
                  onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white placeholder-slate-600"
                  data-testid="entry-notes-input"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 border-slate-700 text-slate-300"
                  onClick={() => setShowAddForm(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-rose-600 hover:bg-rose-500 text-white"
                  onClick={handleSubmit}
                  data-testid="save-blood-sugar-btn"
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        )}
        </div>
        )}
      </main>
    </div>
  );
}
